import { create } from 'zustand';

/** How long a "… is typing" event stays visible without a follow-up. */
export const TYPING_VISIBLE_MS = 5000;

interface TypingEntry {
  name: string;
  until: number;
}

interface MessengerUiState {
  /** The chat open on screen (realtime handlers treat its messages as read when the page has focus). */
  openChatId: string | null;
  /** Whether the open chat shows its newest messages (scrolled to the end). */
  openChatAtEnd: boolean;
  /** People typing per chat. */
  typing: Record<string, Record<string, TypingEntry>>;
  /** Unsent text per chat. */
  drafts: Record<string, string>;
  /** On touch screens: the message whose actions are shown (tap toggles). */
  activeMessageId: string | null;
  /** The chat the user is leaving right now (its "removed" event is expected, not news). */
  leavingChatId: string | null;
  /** Chats the user left or was removed from: their queries stay disabled (they would answer 404). */
  goneChatIds: readonly string[];
  setOpenChat: (chatId: string | null) => void;
  setOpenChatAtEnd: (atEnd: boolean) => void;
  setTyping: (chatId: string, userId: string, name: string, now?: number) => void;
  clearTyping: (chatId: string, userId: string) => void;
  pruneTyping: (now?: number) => void;
  setDraft: (chatId: string, html: string) => void;
  toggleActiveMessage: (messageId: string) => void;
  setLeaving: (chatId: string | null) => void;
  /** Marks a chat as gone (`true`) or available again (`false`, e.g. after being added back). */
  setGone: (chatId: string, gone: boolean) => void;
  /** Forgets everything of the signed-in user (drafts, typing, gone chats): called when they sign out. */
  reset: () => void;
}

const initialUi = {
  openChatId: null,
  openChatAtEnd: true,
  typing: {},
  drafts: {},
  activeMessageId: null,
  leavingChatId: null,
  goneChatIds: [],
} satisfies Partial<MessengerUiState>;

export const useMessengerStore = create<MessengerUiState>()((set) => ({
  ...initialUi,
  setOpenChat: (openChatId) => set({ openChatId, openChatAtEnd: true }),
  setOpenChatAtEnd: (openChatAtEnd) =>
    set((s) => (s.openChatAtEnd === openChatAtEnd ? s : { openChatAtEnd })),
  setTyping: (chatId, userId, name, now = Date.now()) =>
    set((s) => ({
      typing: {
        ...s.typing,
        [chatId]: { ...s.typing[chatId], [userId]: { name, until: now + TYPING_VISIBLE_MS } },
      },
    })),
  clearTyping: (chatId, userId) =>
    set((s) => {
      const chat = s.typing[chatId];
      if (!chat?.[userId]) return s;
      const rest = Object.fromEntries(Object.entries(chat).filter(([id]) => id !== userId));
      return { typing: { ...s.typing, [chatId]: rest } };
    }),
  pruneTyping: (now = Date.now()) =>
    set((s) => {
      let changed = false;
      const typing: MessengerUiState['typing'] = {};
      for (const [chatId, people] of Object.entries(s.typing)) {
        const alive = Object.fromEntries(Object.entries(people).filter(([, entry]) => entry.until > now));
        if (Object.keys(alive).length !== Object.keys(people).length) changed = true;
        if (Object.keys(alive).length > 0) typing[chatId] = alive;
      }
      return changed ? { typing } : s;
    }),
  setDraft: (chatId, html) =>
    set((s) => (s.drafts[chatId] === html ? s : { drafts: { ...s.drafts, [chatId]: html } })),
  toggleActiveMessage: (messageId) =>
    set((s) => ({ activeMessageId: s.activeMessageId === messageId ? null : messageId })),
  setLeaving: (leavingChatId) => set({ leavingChatId }),
  setGone: (chatId, gone) =>
    set((s) => {
      const has = s.goneChatIds.includes(chatId);
      if (has === gone) return s;
      return { goneChatIds: gone ? [...s.goneChatIds, chatId] : s.goneChatIds.filter((id) => id !== chatId) };
    }),
  reset: () => set({ ...initialUi }),
}));

/** Whether the user left (or was removed from) the chat in this session. */
export const useChatGone = (chatId: string | undefined): boolean =>
  useMessengerStore((s) => !!chatId && s.goneChatIds.includes(chatId));

/** Names of the people typing in a chat (stable order). */
export function selectTypingNames(state: MessengerUiState, chatId: string): string[] {
  return Object.values(state.typing[chatId] ?? {})
    .map((entry) => entry.name)
    .sort((a, b) => a.localeCompare(b));
}
